import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { 
  Star, 
  Calendar, 
  Clock, 
  Video, 
  CheckCircle2, 
  Linkedin, 
  Twitter,
  ArrowLeft,
  Shield,
  MessageSquare
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "@/App";
import { useLocation } from "@/contexts/LocationContext";
import { AvailabilityCalendar } from "@/components/AvailabilityCalendar";

// Currency symbols mapping
const getCurrencySymbol = (currency) => {
  const symbols = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    AED: 'AED ',
    INR: '₹',
    SAR: 'SAR ',
    QAR: 'QAR ',
    KWD: 'KWD ',
    BHD: 'BHD ',
    OMR: 'OMR '
  };
  return symbols[currency] || '$';
};

export default function ExpertProfilePage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { userLocation, isIndianUser, paymentProvider } = useLocation();
  const [expert, setExpert] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  
  const [bookingData, setBookingData] = useState({
    scheduled_date: "",
    scheduled_time: "",
    duration_minutes: 60,
    notes: "",
  });

  useEffect(() => {
    fetchExpert();
    fetchReviews();
  }, [id]);

  const fetchExpert = async () => {
    try {
      const response = await axios.get(`${API}/experts/${id}`);
      setExpert(response.data);
    } catch (error) {
      console.error("Error fetching expert:", error);
      toast.error("Expert not found");
      navigate("/experts");
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/expert/${id}`);
      setReviews(response.data);
    } catch (error) {
      console.error("Error fetching reviews:", error);
    }
  };

  const handleBooking = async () => {
    if (!user) {
      toast.error("Please login to book a session");
      navigate("/login");
      return;
    }

    if (!bookingData.scheduled_date || !bookingData.scheduled_time) {
      toast.error("Please select a date and time");
      return;
    }

    setSubmitting(true);
    try {
      // Route to different payment providers based on user location
      if (isIndianUser || paymentProvider === 'razorpay') {
        // Use Razorpay for Indian users
        const response = await axios.post(`${API}/razorpay/create-order`, {
          expert_id: id,
          scheduled_date: bookingData.scheduled_date,
          scheduled_time: bookingData.scheduled_time,
          duration_minutes: parseInt(bookingData.duration_minutes),
          notes: bookingData.notes,
        });

        // Initialize Razorpay checkout
        const options = {
          key: response.data.razorpay_key_id,
          amount: response.data.amount,
          currency: response.data.currency,
          name: "ExpertBase",
          description: `Session with ${expert.name}`,
          order_id: response.data.order_id,
          handler: async function (razorpayResponse) {
            // Verify payment on backend
            try {
              const verifyResponse = await axios.post(`${API}/razorpay/verify-payment`, {
                razorpay_order_id: razorpayResponse.razorpay_order_id,
                razorpay_payment_id: razorpayResponse.razorpay_payment_id,
                razorpay_signature: razorpayResponse.razorpay_signature,
              });
              
              if (verifyResponse.data.success) {
                toast.success("Payment successful!");
                navigate(`/booking-success?session_id=${verifyResponse.data.booking_id}`);
              }
            } catch (verifyError) {
              console.error("Payment verification failed:", verifyError);
              toast.error("Payment verification failed. Please contact support.");
            }
          },
          prefill: {
            name: user.name,
            email: user.email,
          },
          theme: {
            color: "#D4AF37",
          },
        };

        // Load Razorpay script and open checkout
        if (window.Razorpay) {
          const razorpay = new window.Razorpay(options);
          razorpay.open();
        } else {
          toast.error("Payment service unavailable. Please try again.");
        }
      } else {
        // Use Stripe for all other users
        const response = await axios.post(`${API}/bookings`, {
          expert_id: id,
          scheduled_date: bookingData.scheduled_date,
          scheduled_time: bookingData.scheduled_time,
          duration_minutes: parseInt(bookingData.duration_minutes),
          notes: bookingData.notes,
          origin_url: window.location.origin,
        });

        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error("Error creating booking:", error);
      toast.error(error.response?.data?.detail || "Failed to create booking");
    } finally {
      setSubmitting(false);
    }
  };

  const calculatePrice = () => {
    if (!expert) return 0;
    const hours = parseInt(bookingData.duration_minutes) / 60;
    return (expert.hourly_rate * hours).toFixed(2);
  };

  // Generate available time slots
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 9; hour <= 17; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
      slots.push(`${hour.toString().padStart(2, '0')}:30`);
    }
    return slots;
  };

  // Get minimum date (tomorrow)
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  // Handle slot selection from AvailabilityCalendar
  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    setBookingData(prev => ({
      ...prev,
      scheduled_date: slot.date,
      scheduled_time: slot.time,
      duration_minutes: slot.duration
    }));
  };

  // Handle booking with selected slot
  const handleBookWithSlot = async () => {
    if (!user) {
      toast.error(t('auth.errors.loginRequired', 'Please login to book a session'));
      navigate("/login");
      return;
    }

    if (!selectedSlot) {
      toast.error("Please select a time slot");
      return;
    }

    setSubmitting(true);
    try {
      // Route to different payment providers based on user location
      if (isIndianUser || paymentProvider === 'razorpay') {
        // Use Razorpay for Indian users
        const response = await axios.post(`${API}/razorpay/create-order`, {
          expert_id: id,
          scheduled_date: bookingData.scheduled_date,
          scheduled_time: bookingData.scheduled_time,
          duration_minutes: parseInt(bookingData.duration_minutes),
          notes: bookingData.notes,
        });

        // Initialize Razorpay checkout
        const options = {
          key: response.data.razorpay_key_id,
          amount: response.data.amount,
          currency: response.data.currency,
          name: "ExpertBase",
          description: `Session with ${expert.name}`,
          order_id: response.data.order_id,
          handler: async function (razorpayResponse) {
            try {
              const verifyResponse = await axios.post(`${API}/razorpay/verify-payment`, {
                razorpay_order_id: razorpayResponse.razorpay_order_id,
                razorpay_payment_id: razorpayResponse.razorpay_payment_id,
                razorpay_signature: razorpayResponse.razorpay_signature,
              });
              
              if (verifyResponse.data.success) {
                toast.success("Payment successful!");
                navigate(`/booking-success?session_id=${verifyResponse.data.booking_id}`);
              }
            } catch (verifyError) {
              console.error("Payment verification failed:", verifyError);
              toast.error("Payment verification failed. Please contact support.");
            }
          },
          prefill: {
            name: user.name,
            email: user.email,
          },
          theme: {
            color: "#D4AF37",
          },
        };

        if (window.Razorpay) {
          const razorpay = new window.Razorpay(options);
          razorpay.open();
        } else {
          toast.error("Payment service unavailable. Please try again.");
        }
      } else {
        // Use Stripe for all other users
        const response = await axios.post(`${API}/bookings`, {
          expert_id: id,
          scheduled_date: bookingData.scheduled_date,
          scheduled_time: bookingData.scheduled_time,
          duration_minutes: parseInt(bookingData.duration_minutes),
          notes: bookingData.notes,
          origin_url: window.location.origin,
        });

        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error("Error creating booking:", error);
      toast.error(error.response?.data?.detail || "Failed to create booking");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian">
        <Navbar />
        <div className="pt-32 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-gold-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!expert) return null;

  return (
    <div className="min-h-screen bg-obsidian">
      <Navbar />
      
      <section className="pt-28 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Back button */}
          <button 
            onClick={() => navigate("/experts")}
            className="flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
            data-testid="back-to-experts"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to experts
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Profile Header */}
              <motion.div 
                className="flex flex-col md:flex-row gap-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {expert.image_url && expert.image_url.length > 0 && !expert.stay_anonymous ? (
                  <div className="w-40 h-40 rounded-sm overflow-hidden" data-testid="expert-image">
                    <img 
                      src={expert.image_url.startsWith('http') ? expert.image_url : `${process.env.REACT_APP_BACKEND_URL}${expert.image_url}`}
                      alt={expert.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="w-full h-full bg-gold-500/20 items-center justify-center hidden">
                      <span className="font-display text-5xl text-gold-500">
                        {expert.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div 
                    className="w-40 h-40 bg-gold-500/20 rounded-sm flex items-center justify-center"
                    data-testid="expert-image"
                  >
                    <span className="font-display text-5xl text-gold-500">
                      {expert.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
                    </span>
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {expert.verified && (
                      <span className="inline-flex items-center gap-1 text-xs bg-gold-500/20 text-gold-500 px-2 py-1 rounded">
                        <CheckCircle2 className="w-3 h-3" />
                        VERIFIED
                      </span>
                    )}
                  </div>
                  <h1 className="font-display text-4xl mb-2" data-testid="expert-name">{expert.name}</h1>
                  <p className="text-xl text-slate-400 mb-4" data-testid="expert-title">{expert.title}</p>
                  
                  <div className="flex flex-wrap items-center gap-6 text-sm">
                    <div className="flex items-center gap-2">
                      <Star className="w-5 h-5 fill-gold-500 text-gold-500" />
                      <span className="font-semibold">{expert.rating}</span>
                      <span className="text-slate-500">({reviews.length} reviews)</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-400">
                      <Video className="w-5 h-5" />
                      <span>{expert.total_sessions} sessions</span>
                    </div>
                  </div>

                  {/* Social Links */}
                  <div className="flex gap-3 mt-4">
                    {expert.linkedin && !expert.stay_anonymous ? (
                      <a 
                        href={expert.linkedin} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="p-2 bg-[#0A66C2]/20 hover:bg-[#0A66C2]/30 border border-[#0A66C2]/30 hover:border-[#0A66C2] rounded-sm transition-colors"
                        data-testid="linkedin-profile-btn"
                      >
                        <Linkedin className="w-5 h-5 text-[#0A66C2]" />
                      </a>
                    ) : expert.stay_anonymous ? (
                      <button 
                        onClick={() => toast.info("This expert has chosen to remain anonymous")}
                        className="p-2 bg-slate-800/50 border border-slate-700 rounded-sm cursor-not-allowed opacity-50"
                        title="Expert is anonymous"
                      >
                        <Linkedin className="w-5 h-5 text-slate-500" />
                      </button>
                    ) : null}
                    {expert.twitter && !expert.stay_anonymous && (
                      <a 
                        href={expert.twitter} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="p-2 bg-charcoal border border-slate-800 hover:border-gold-500 rounded-sm transition-colors"
                      >
                        <Twitter className="w-5 h-5" />
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>

              {/* Bio */}
              <motion.div 
                className="bg-charcoal border border-slate-800 p-8 rounded-sm"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <h2 className="font-display text-2xl mb-4">About</h2>
                <p className="text-slate-300 leading-relaxed" data-testid="expert-bio">{expert.bio}</p>
              </motion.div>

              {/* Expertise & Industries */}
              <motion.div 
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="bg-charcoal border border-slate-800 p-6 rounded-sm">
                  <h3 className="font-display text-xl mb-4">Expertise</h3>
                  <div className="flex flex-wrap gap-2">
                    {(expert.expertise || []).map((exp, i) => (
                      <span 
                        key={i} 
                        className="px-3 py-1.5 bg-gold-500/10 text-gold-500 text-sm rounded-full"
                      >
                        {exp}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="bg-charcoal border border-slate-800 p-6 rounded-sm">
                  <h3 className="font-display text-xl mb-4">Industries</h3>
                  <div className="flex flex-wrap gap-2">
                    {(expert.industries || []).map((ind, i) => (
                      <span 
                        key={i} 
                        className="px-3 py-1.5 bg-slate-800 text-slate-300 text-sm rounded-full"
                      >
                        {ind}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Reviews */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <h2 className="font-display text-2xl mb-6">Reviews ({reviews.length})</h2>
                {reviews.length > 0 ? (
                  <div className="space-y-4">
                    {reviews.map((review) => (
                      <div 
                        key={review.id} 
                        className="bg-charcoal border border-slate-800 p-6 rounded-sm"
                      >
                        <div className="flex items-center gap-4 mb-3">
                          <div className="flex gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star 
                                key={i} 
                                className={`w-4 h-4 ${i < review.rating ? 'fill-gold-500 text-gold-500' : 'text-slate-600'}`}
                              />
                            ))}
                          </div>
                          <span className="text-slate-500 text-sm">{review.user_name}</span>
                        </div>
                        {review.comment && (
                          <p className="text-slate-300">{review.comment}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-charcoal border border-slate-800 p-8 rounded-sm text-center">
                    <MessageSquare className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">No reviews yet. Be the first!</p>
                  </div>
                )}
              </motion.div>
            </div>

            {/* Booking Sidebar */}
            <div className="lg:col-span-1">
              {/* Check if user is the expert themselves */}
              {user && expert.user_id === user.id ? (
                <motion.div 
                  className="sticky top-28 bg-charcoal border border-slate-800 p-6 rounded-sm"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  <div className="text-center py-4">
                    <p className="text-slate-400 mb-2">{t('profile.booking.ownProfile', 'This is your own profile')}</p>
                    <Button 
                      onClick={() => navigate("/expert/dashboard")}
                      variant="link"
                      className="text-gold-500 hover:text-gold-400"
                    >
                      {t('profile.booking.goToDashboard', 'Go to your dashboard →')}
                    </Button>
                  </div>
                </motion.div>
              ) : (
                <motion.div 
                  className="sticky top-28 space-y-4"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  {/* Availability Calendar */}
                  <AvailabilityCalendar
                    expertId={id}
                    expertName={expert.name}
                    hourlyRate={expert.hourly_rate}
                    currency={expert.currency || "USD"}
                    onSlotSelect={handleSlotSelect}
                    selectedSlot={selectedSlot}
                  />

                  {/* Notes Section */}
                  {selectedSlot && (
                    <motion.div 
                      className="bg-charcoal border border-slate-800 p-4 rounded-sm"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <Label className="text-sm text-slate-400 mb-2 block">
                        {t('profile.booking.discussTopic', 'What would you like to discuss? (Optional)')}
                      </Label>
                      <Textarea
                        placeholder={t('profile.booking.discussPlaceholder', "Share some context about what you'd like help with...")}
                        value={bookingData.notes}
                        onChange={(e) => setBookingData(prev => ({ ...prev, notes: e.target.value }))}
                        className="bg-obsidian border-slate-700 min-h-[80px] text-sm"
                        data-testid="booking-notes"
                      />
                    </motion.div>
                  )}

                  {/* Book Button */}
                  <Button 
                    onClick={handleBookWithSlot}
                    disabled={submitting || !selectedSlot}
                    className="w-full bg-gold-500 hover:bg-gold-400 text-black font-semibold py-6 rounded-sm btn-shine disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="book-session-btn"
                  >
                    {submitting ? (
                      <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Calendar className="w-5 h-5 mr-2" />
                        {selectedSlot ? t('profile.booking.proceedPayment', 'Proceed to Payment') : t('profile.booking.bookSession', 'Select a Time to Book')}
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-slate-500 text-center">
                    {t('profile.booking.freeCancellation', 'Free cancellation up to 24 hours before')}
                  </p>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
